import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Typography, Alert, Tag } from 'antd';
import WatercolorLoader from '../components/WatercolorLoader';
import { BookOutlined, PlusOutlined } from '@ant-design/icons';
import { listProjects } from '../illustration/api';
import { getDefaultProvider } from '../illustration/providers/registry';

const { Title, Text, Paragraph } = Typography;

const HomePage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const provider = getDefaultProvider();

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listProjects();
      setProjects(data.projects || []);
    } catch (e) {
      setError(e.message || 'Could not load projects. Is the illustration API running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    document.title = 'Illustration Studio';
  }, []);

  return (
    <div className="workflow-shell__content">
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <Title
          level={1}
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-display)',
            letterSpacing: 'var(--tracking-display)',
            margin: 0,
          }}
        >
          Illustration Studio
        </Title>
        <Text type="secondary">
          Provider: <Tag>{provider?.label || 'none'}</Tag>
          {provider?.defaultModel ? <Tag>{provider.defaultModel}</Tag> : null}
        </Text>
      </div>

      <Paragraph style={{ color: 'var(--color-text-secondary)', maxWidth: 'var(--measure-prose)' }}>
        Pick a project from the sidebar, or create a new book seeded by an Illustration Bible.
        Chapters carry treatments/prompts; pages are authored at 72 dpi and exported at 300 dpi.
      </Paragraph>

      <div style={{ marginBottom: 'var(--space-6)' }}>
        <Link to="/new">
          <Button type="primary" icon={<PlusOutlined />} size="large">
            Create New
          </Button>
        </Link>
      </div>

      {error && (
        <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />
      )}

      <Title level={3} style={{ fontFamily: 'var(--font-display)', marginBottom: 'var(--space-3)' }}>
        Projects
      </Title>

      {loading ? (
        <div className="watercolor-loader-slot">
          <WatercolorLoader />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {projects.map((p) => (
            <Link
              key={p.id}
              to={`/projects/${p.id}/chapters`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                padding: 'var(--space-4)',
                background: 'var(--color-bg-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              <BookOutlined style={{ fontSize: 22, color: 'var(--color-accent-text)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <Text strong style={{ display: 'block' }}>{p.title}</Text>
                <Text type="secondary" style={{ fontSize: 'var(--text-caption)' }}>
                  Bible v{p.bibleVersion} · {p.status}
                  {p.resolution ? ` · ${p.resolution.workDpi}/${p.resolution.exportDpi} dpi` : ''}
                </Text>
              </div>
            </Link>
          ))}
          {!projects.length && (
            <Text type="secondary">No projects yet — use Create New to import a bible.</Text>
          )}
        </div>
      )}
    </div>
  );
};

export default HomePage;
